import { createDAppKit } from '@mysten/dapp-kit-react';
import { runtimeConfig } from './runtimeConfig';
import { getSuiClient } from './client';
import { validateNetwork } from './networkUtils';
import { NETWORKS } from '../types/commonTypes';
/**
 * localStorage key dApp Kit persists the selected wallet + address under.
 *
 * This is dApp Kit's own default value, passed explicitly rather than left implicit
 * because hooks/walletCompat.ts reads the key to tell "auto-connect hasn't started yet"
 * apart from "there was never a session to restore". Pinning it here keeps the two in
 * sync and makes it obvious that changing it signs every connected user out.
 */
export const WALLET_SESSION_STORAGE_KEY = 'mysten-dapp-kit:selected-wallet-and-address';
/*
 * On the removal of useWalletPersistConnection / clearPreferredWallet (v1-era):
 *
 * That hook kept its own `alphafi:preferred-wallet` copy of the connected wallet because
 * dapp-kit v1 erased its reconnect memory when the connected wallet unregistered — which
 * the Slush web wallet does by design as soon as the Slush extension injects
 * (@mysten/slush-wallet still does this: it unregisters on SUI_WALLET_EXTENSION_ID
 * registering, and when its metadata reports enabled: false).
 *
 * v2 no longer loses that memory. On unregister, manageWalletConnection resets the
 * connection store to `disconnected` but does NOT touch the persisted session; only an
 * explicit disconnectWallet() removes the storage key. So the durable copy is redundant,
 * and clearPreferredWallet() is subsumed by disconnectWallet() clearing the key itself.
 *
 * Residual gap: autoConnect stops listening once it has restored a session, so an
 * unregister -> re-register handoff is not re-attempted within the same page session (the
 * next load restores it from the surviving key). Closing that would need a silent
 * reconnect, and v2 deliberately does not expose one — its connectWallet args are
 * Omit<StandardConnectInput, 'silent'>, so a retry loop would risk a wallet popup. Left
 * for upstream rather than reintroducing the hook against a non-public API.
 */
// Single dApp Kit 2.0 instance for the whole app. Wallet actions
// (signAndExecuteTransaction, connectWallet, disconnectWallet, switchAccount, …)
// are called directly on this instance via the useDAppKit() hook.
export const dAppKit = createDAppKit({
    networks: [...NETWORKS],
    defaultNetwork: validateNetwork(runtimeConfig.getDefaultNetwork()),
    storageKey: WALLET_SESSION_STORAGE_KEY,
    // autoConnect defaults to true in v2, with a built-in grace period
    // (autoConnectTimeout, default 5000ms) that waits out the wallet-registration race.
    //
    // Reuse client.ts's per-network cache instead of constructing a client here, so
    // signing/building and plain chain reads share one gRPC-web transport per network
    // (and one endpoint resolution, including the runtime override).
    createClient(network) {
        return getSuiClient(network);
    },
    // Keep the Slush web wallet branded as "Alphafi" (legacy: slushWallet={{ name: 'Alphafi' }}).
    slushWalletConfig: { appName: 'Alphafi' },
});
